/**
 * NEW_UX timeline-item editor — design tokens.
 *
 * Mirrors the values extracted from the /task-editor design prototype (Layout A). This is
 * a self-contained visual system for the new editor shell; it does not replace the app's
 * MUI theme (existing leaf editors keep the app theme). Signature look: controls use inset
 * box-shadow "rings" instead of borders, flipping to a blue ring on select/focus.
 *
 * Convention: prefer these tokens for anything they cover. Reach for the classic emotion
 * theme (`p.theme.custom.colors.*`) only for named semantic colors this palette doesn't
 * have yet (e.g. a specific tinted background or accent called out in a design spec) —
 * that's an intentional, ongoing gap in this token set, not a mistake to "fix" locally.
 */

export const tokens = {
  color: {
    // Blues (primary)
    blue050: '#EBF3FE',
    blue100: '#EDF5FD',
    blue300: '#93C5FD',
    blue500: '#0E78E4', // primary
    blue600: '#0061C4',
    blue700: '#1E40AF',
    // Brand green — preview chrome / confirm actions / "everyone" segment ONLY
    green: '#127E63',
    green050: '#ECFAF4',
    greenBorder: '#BFE5CE',
    // Accents
    purple500: '#7C3AED',
    yellow050: '#FEF9EB',
    yellow600: '#D97706',
    red500: '#EF4444',
    red600: '#DC2626',
    // Neutrals (blue-gray family)
    n050: '#F9FAFB',
    n100: '#F1F3F5',
    n200: '#E5E7EB',
    n300: '#D1D5DB',
    n400: '#C0C4CC',
    n500: '#6B7280',
    n600: '#4A5870',
    n700: '#374151',
    n800: '#1A2538',
    n900: '#0E1A2B',
    // Text
    textPrimary: '#0E1A2B',
    textSecondary: '#8C9BAF',
    textMuted: '#9CA3AF',
    // App type/greys reused by the editor chrome (mirror muiTheme.ts): 'black' is the
    // app's text.primary/common.black; grey500/grey700 are palette.grey[500|700].
    black: '#08121A',
    grey100: '#F8FAFB',
    grey500: '#778088',
    grey700: '#494F55',
    // Surfaces / borders
    borderSubtle: '#E5E7EB',
    canvas: '#F4F6F8',
    white: '#FFFFFF'
  },
  radius: { sm: 4, md: 8, lg: 12, pill: 999 },
  /** 4px spacing scale. space(3) === 12px. */
  space: (n: number) => `${n * 4}px`,
  font: {
    size: {
      eyebrow: 10,
      badge: 11,
      hint: 12,
      body: 13,
      title: 14,
      sheet: 15,
      head: 18
    },
    // Unusually light weight system: 300 body / 600 labels / 700 titles+eyebrows.
    weight: { body: 300, label: 600, title: 700 },
    /**
     * Mirrors themes/muiTheme.ts's `typography` variants exactly (base values — MUI's
     * own >1281px breakpoint bump on body1/body2/caption isn't reproduced here). Use
     * these instead of `size`/`weight` above wherever the surface needs to read as part
     * of the same type system as the rest of the (classic MUI) app, rather than the
     * kit's own denser, heavier-weight scale.
     */
    variant: {
      h1: { fontSize: 40, lineHeight: 48, fontWeight: 400, letterSpacing: '-0.01em' },
      h2: { fontSize: 32, lineHeight: 40, fontWeight: 400 },
      h3: { fontSize: 28, lineHeight: 36, fontWeight: 400 },
      h4: { fontSize: 22, lineHeight: 30, fontWeight: 400 },
      h5: { fontSize: 18, lineHeight: 24, fontWeight: 400 },
      // muiTheme.ts's h6/body1/body2 are responsive (15px below 1281px viewport
      // width, 16px at/above it) — this kit has no media-query support, so these
      // mirror the >=1281px desktop value that this admin app is built for.
      h6: { fontSize: 16, lineHeight: 22, fontWeight: 400 },
      body1: { fontSize: 16, lineHeight: 22, fontWeight: 200 },
      body2: { fontSize: 16, lineHeight: 22, fontWeight: 200 },
      caption: { fontSize: 14, lineHeight: 19, fontWeight: 200, color: '#778088' },
      /** Not part of muiTheme.ts — new, kit-only variant for small uppercase section labels. */
      h7: {
        fontSize: 14,
        lineHeight: 20,
        fontWeight: 400,
        textTransform: 'uppercase' as const
      }
    },
    /** Matches muiTheme.ts's typography.fontWeightLight/Regular/Medium/Bold. */
    weightScale: { light: 200, regular: 200, medium: 400, bold: 400 }
  },
  shadow: {
    sm: '0 1px 3px rgba(0,0,0,.08), 0 0 0 1px rgba(0,0,0,.04)',
    md: '0 4px 16px rgba(0,0,0,.16), 0 0 0 1px rgba(0,0,0,.06)',
    lg: '0 12px 32px rgba(0,0,0,.12)'
  },
  layout: {
    railWidth: 60,
    railBtn: 40,
    topBarHeight: 64,
    inspectorWidth: 340,
    sheetWidth: 600
  }
} as const;

/**
 * Segment colour palette — the prototype's `SEG_COLORS` (editor-panels.jsx
 * 280-286), copied verbatim as tokens. Used for the per-segment swatch/colour
 * picker and round-robin colour assignment for new segments.
 */
export const segmentColors: ReadonlyArray<{ name: string; hex: string }> = [
  { name: 'Coral', hex: '#c63e16' },
  { name: 'Turquoise', hex: '#3aa189' },
  { name: 'Terracotta', hex: '#ad5534' },
  { name: 'Teal', hex: '#32aca8' },
  { name: 'Cocoa', hex: '#8d6147' },
  { name: 'Ocean', hex: '#2486af' },
  { name: 'Umber', hex: '#976233' },
  { name: 'Blue', hex: '#1867c3' },
  { name: 'Amber', hex: '#c67216' },
  { name: 'Denim', hex: '#48639d' },
  { name: 'Sand', hex: '#a67e3d' },
  { name: 'Mulberry', hex: '#9e2e74' },
  { name: 'Mustard', hex: '#ac8d2a' },
  { name: 'Cobalt', hex: '#2034b1' },
  { name: 'Moss', hex: '#6b974a' },
  { name: 'Rose', hex: '#b6284c' },
  { name: 'Yellow', hex: '#dcb000' },
  { name: 'Periwinkle', hex: '#4f3d9e' },
  { name: 'Sage', hex: '#519259' },
  { name: 'Wine', hex: '#98414f' }
];

/** Hairline "border" done as an inset ring, so selection can override it cleanly. */
export const ring = (color: string = tokens.color.borderSubtle) =>
  `inset 0 0 0 1px ${color}`;

/** Selected/focused ring (1.5px blue). Interactive/selected accent is blue600. */
export const ringActive = `inset 0 0 0 1.5px ${tokens.color.blue600}`;
